import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CodeIcon from "@mui/icons-material/Code";

const WidgetModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;
  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState({
    name: "",
    type: "Chat",
    assistant: "",
    position: "Bottom Right",
    primaryColor: "#4f3edb",
    buttonText: "Chat with us",
    welcomeMessage: "Hello! How can I help you today?",
    placeholderText: "Type your message...",
    width: 400,
    height: 600,
    showAvatar: true,
    allowFileUpload: false,
  });

  const [generatedId, setGeneratedId] = useState("");

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        type: data.type || "Chat",
        assistant: data.assistant || "",
        position: data.position || "Bottom Right",
        primaryColor: data.primaryColor || "#4f3edb",
        buttonText: data.buttonText || "Chat with us",
        welcomeMessage: data.welcomeMessage || "Hello! How can I help you today?",
        placeholderText: data.placeholderText || "Type your message...",
        width: data.width || 400,
        height: data.height || 600,
        showAvatar: data.showAvatar !== false,
        allowFileUpload: data.allowFileUpload || false,
      });
      setGeneratedId(data.id || "");
    } else {
      // Generate widget ID for new widget
      const id = "widget_" + Math.random().toString(36).substr(2, 9);
      setGeneratedId(id);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const getEmbedCode = () => {
    return `<!-- VAPI Widget Embed Code -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = 'https://cdn.vapi.com/widget.js';
    script.async = true;
    script.dataset.widgetId = '${generatedId}';
    script.dataset.position = '${formData.position.toLowerCase().replace(" ", "-")}';
    script.dataset.primaryColor = '${formData.primaryColor}';
    document.head.appendChild(script);
  })();
</script>`;
  };

  const getReactCode = () => {
    return `import { VAPIWidget } from '@vapi/react';

function App() {
  return (
    <VAPIWidget
      widgetId="${generatedId}"
      position="${formData.position.toLowerCase().replace(" ", "-")}"
      primaryColor="${formData.primaryColor}"
      assistant="${formData.assistant}"
    />
  );
}`;
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert("Code copied to clipboard!");
  };

  const handleSubmit = () => {
    onSave({ ...formData, id: generatedId });
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: "",
      type: "Chat",
      assistant: "",
      position: "Bottom Right",
      primaryColor: "#4f3edb",
      buttonText: "Chat with us",
      welcomeMessage: "Hello! How can I help you today?",
      placeholderText: "Type your message...",
      width: 400,
      height: 600,
      showAvatar: true,
      allowFileUpload: false,
    });
    setActiveTab(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Widget" : "Create Widget"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Configuration" />
          <Tab label="Embed Code" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Widget Information
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Widget Name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  placeholder="e.g., Website Chat Widget"
                />
                <TextField
                  fullWidth
                  label="Widget ID"
                  value={generatedId}
                  disabled
                  helperText="Unique identifier for this widget"
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Widget Type</InputLabel>
                    <Select
                      value={formData.type}
                      label="Widget Type"
                      onChange={(e) => handleChange("type", e.target.value)}
                    >
                      <MenuItem value="Chat">Chat Widget</MenuItem>
                      <MenuItem value="Voice">Voice Widget</MenuItem>
                      <MenuItem value="Hybrid">Hybrid (Chat + Voice)</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Select Assistant</InputLabel>
                    <Select
                      value={formData.assistant}
                      label="Select Assistant"
                      onChange={(e) => handleChange("assistant", e.target.value)}
                    >
                      <MenuItem value="Customer Support Agent">Customer Support Agent</MenuItem>
                      <MenuItem value="Sales Agent">Sales Agent</MenuItem>
                      <MenuItem value="Technical Support">Technical Support</MenuItem>
                      <MenuItem value="General Assistant">General Assistant</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Appearance */}
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Appearance
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={formData.position}
                    label="Position"
                    onChange={(e) => handleChange("position", e.target.value)}
                  >
                    <MenuItem value="Bottom Right">Bottom Right</MenuItem>
                    <MenuItem value="Bottom Left">Bottom Left</MenuItem>
                    <MenuItem value="Top Right">Top Right</MenuItem>
                    <MenuItem value="Top Left">Top Left</MenuItem>
                  </Select>
                </FormControl>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <TextField
                    label="Primary Color"
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => handleChange("primaryColor", e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    fullWidth
                    label="Primary Color (Hex)"
                    value={formData.primaryColor}
                    onChange={(e) => handleChange("primaryColor", e.target.value)}
                    placeholder="#4f3edb"
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Width (px)"
                    type="number"
                    value={formData.width}
                    onChange={(e) => handleChange("width", parseInt(e.target.value))}
                    InputProps={{ inputProps: { min: 300, max: 600 } }}
                  />
                  <TextField
                    fullWidth
                    label="Height (px)"
                    type="number"
                    value={formData.height}
                    onChange={(e) => handleChange("height", parseInt(e.target.value))}
                    InputProps={{ inputProps: { min: 400, max: 800 } }}
                  />
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Messages */}
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Messages & Text
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Button Text"
                  value={formData.buttonText}
                  onChange={(e) => handleChange("buttonText", e.target.value)}
                  placeholder="Chat with us"
                />
                <TextField
                  fullWidth
                  label="Welcome Message"
                  value={formData.welcomeMessage}
                  onChange={(e) => handleChange("welcomeMessage", e.target.value)}
                  multiline
                  rows={2}
                  placeholder="Hello! How can I help you today?"
                />
                <TextField
                  fullWidth
                  label="Input Placeholder"
                  value={formData.placeholderText}
                  onChange={(e) => handleChange("placeholderText", e.target.value)}
                  placeholder="Type your message..."
                />
              </Box>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* HTML/JavaScript Embed */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  HTML/JavaScript Embed
                </Typography>
                <CustomButton
                  icon={<ContentCopyIcon />}
                  iconPosition="left"
                  text="Copy"
                  size="small"
                  variant="outlined"
                  onClick={() => handleCopyCode(getEmbedCode())}
                />
              </Box>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E0E0E0",
                  position: "relative",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CodeIcon sx={{ color: "text.secondary", mt: 0.5 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      flex: 1,
                      fontSize: 12,
                    }}
                  >
                    {getEmbedCode()}
                  </Typography>
                </Box>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Add this code before the closing &lt;/body&gt; tag on your website
              </Typography>
            </Box>

            <Divider />

            {/* React Component */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  React Component
                </Typography>
                <CustomButton
                  icon={<ContentCopyIcon />}
                  iconPosition="left"
                  text="Copy"
                  size="small"
                  variant="outlined"
                  onClick={() => handleCopyCode(getReactCode())}
                />
              </Box>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E0E0E0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <CodeIcon sx={{ color: "text.secondary", mt: 0.5 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                      flex: 1,
                      fontSize: 12,
                    }}
                  >
                    {getReactCode()}
                  </Typography>
                </Box>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Install the package: npm install @vapi/react
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text={isEdit ? "Update Widget" : "Create Widget"}
          onClick={handleSubmit}
          disabled={!formData.name.trim() || !formData.assistant}
        />
      </DialogActions>
    </Dialog>
  );
};

export default WidgetModal;
